/**
 * Built-in formula channels — prebuilt derived channels surfaced in the Formulas
 * picker alongside the user's own saved formulas.
 *
 * These are ordinary members of the `formulas[]` array in App.jsx, so they ride the
 * exact same path as user formulas: the `formula_trace` endpoint evaluates the
 * expression over each lap and the result overlays per-lap like the speed trace.
 * They are read-only (no edit/delete) and are NOT persisted to the user's formula
 * library — they are merged in on load and identified by `builtin: true`.
 *
 * Each entry mirrors the FormulaOut shape the rest of the code expects. IDs use
 * underscores (no colon) because the id is interpolated into panel keys
 * (`showFormula_${id}` / `fml_${id}`).
 *
 * Expressions use the aliases the formula engine exposes (lat_g, lon_g, …); see
 * backend/app/core/formula_engine.py. Sessions missing a referenced channel surface
 * the normal graceful formula-error state.
 */
export const BUILTIN_FORMULAS = [
  {
    id: 'builtin_traction',
    name: 'Traction (GSum)',
    expression: 'sqrt(lat_g**2 + lon_g**2)',
    unit: 'g',
    color: '#f5a623',
    description: 'GSum — combined lateral + longitudinal G — how hard the tire is working at each point of the lap.',
    builtin: true,
  },
  {
    id: 'builtin_smoothness',
    name: 'Smoothness (lat-G variability)',
    expression: 'rolling_std(lat_g, 20)',
    unit: 'g',
    color: '#4f9cf5',
    description: 'Rolling variability of lateral G — lower is smoother. Spikes mark abrupt steering inputs.',
    builtin: true,
  },
  {
    id: 'builtin_drivestate',
    name: 'Drive state (brake/coast/accel)',
    expression: '(lon_g > 0.1) * 1.0 - (lon_g < -0.1) * 1.0',
    unit: '',
    color: '#57c785',
    description: 'Tri-state from longitudinal G: +1 accelerating, 0 coasting, -1 braking — shows where each phase occurs.',
    builtin: true,
  },
];

/** True when the given formula is a prebuilt built-in (read-only). */
export const isBuiltinFormula = (f) => !!(f && f.builtin);
